// External libraries
import React, { useRef, useState } from 'react';
import { func } from 'prop-types';
import { useStoreState } from '@/store';
import {
  CustomInputContainer,
  FloatingLabelInput,
  genericValidationFn,
  Tooltip,
  // @ts-expect-error untyped import
} from '@checkout/react-components';

// Internal modules
import { features, getContent, getExperiment } from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import {
  customInputContainerStyles,
  EmailAndTooltipContainer,
  inputContainerStyles,
  inputStyleOverrides,
  tooltipStyleOverrides,
} from './styles';
import { emailLookup } from '@/lib/emailLookup';
import { ValueAsFieldLabel } from '@/components/Common/ValueAsFieldLabel';
import { logFptiForEmailChangeButtonClick } from './fpti';
import { LogInLink } from '../../Billing/Components';
import { InfoMessage } from '../../Common/PageMessages';
import type { HandlersTypes, InputOnBlurChange } from '@/types';
import type { InputOnChange } from '@/types';

const Email = ({
  blurHandler,
  focusHandler,
  changeHandler,
}: HandlersTypes): JSX.Element => {
  useMountLogging('Email');
  const [isTooltipDisplayed, setIsTooltipDisplayed] = useState(false);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const theme = features.get('rebrandFormFieldsTheme');

  const isFormSubmitting = useStoreState(state => state.ui.isFormSubmitting);
  const isGooglePayLaterOnly = features.isActive('isGooglePayLaterOnly');
  const showMessage = useStoreState(
    state => state.ui.showIsExistingAccountEmailMessage,
  );

  const emailState = useStoreState(state => state.form.email);
  const onboardOption = useStoreState(state => state.form.onboardOption);
  const { displayError, error, ...partialEmailState } = emailState;

  const shouldShowOptionalUpsellHalfSheet = features.hasFeature(
    'shouldShowOptionalUpsellHalfSheet',
  );
  // Product decision is to lookup email in all countries for Google Pay Later use cases.
  const shouldLookupEmail =
    getExperiment('shouldShowOTP') ||
    shouldShowOptionalUpsellHalfSheet ||
    getExperiment('suppressSignup') ||
    isGooglePayLaterOnly;

  const customBlurHandler: InputOnBlurChange = e => {
    if (shouldLookupEmail) {
      emailLookup(e.target.value);
    }
    blurHandler(e);
  };

  /*
    If the tooltip is clicked directly, this function will ensure it is closed
    and that focus returns to the info button.
  */
  const tooltipClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsTooltipDisplayed(false);
    infoButtonRef.current?.focus();
  };

  /*
    Closes the tooltip if anything besides the info button or tooltip itself
    get clicked
  */
  const tooltipBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      e.target?.id !== 'email-tooltip' &&
      e.relatedTarget?.id !== 'emailInfoIcon'
    ) {
      setIsTooltipDisplayed(false);
    }
  };

  /**
   * Check if change is autofill
   */
  const onChange: InputOnChange = (e, ...rest) => {
    const emailInput = e.target.value;
    // Check if user focus is on email input to trigger lookup
    const activeElm = document.activeElement;
    if (activeElm && 'type' in activeElm && activeElm.type !== 'email') {
      // Verify this is a valid email
      const { isValid } = genericValidationFn(emailInput, {
        props: { required: true, type: 'email' },
      });

      // Fire the lookup for valid email addresses
      if (isValid) {
        emailLookup(emailInput);
      }
    }

    changeHandler(e, ...rest);
  };

  return (
    <>
      <ValueAsFieldLabel
        onClick={logFptiForEmailChangeButtonClick}
        value={emailState.value}
      >
        {({ shouldAutoFocus }) => (
          <EmailAndTooltipContainer>
            <CustomInputContainer
              blurHandler={customBlurHandler}
              displayError={displayError}
              error={error}
              focusHandler={focusHandler}
              inputContainerStyleOverrides={customInputContainerStyles}
              inputId="email"
              theme={theme}
            >
              {(focusAndBlurHandler: boolean) => (
                <>
                  <FloatingLabelInput
                    autoComplete="email"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={shouldAutoFocus}
                    blurHandler={focusAndBlurHandler}
                    changeHandler={onChange}
                    containerStyleOverrides={inputContainerStyles}
                    disabled={isFormSubmitting}
                    focusHandler={focusAndBlurHandler}
                    hasError={displayError}
                    id="email"
                    inputStyleOverrides={inputStyleOverrides}
                    label={getContent('fieldLabels.email')}
                    name="email"
                    required={true}
                    theme={theme === 'v8' ? 'v8-minimal' : theme}
                    type="email"
                    {...partialEmailState}
                  />
                </>
              )}
            </CustomInputContainer>

            {isTooltipDisplayed && (
              <Tooltip
                aria-live="polite"
                arrowPosition="top"
                id="email-tooltip"
                onBlur={tooltipBlurHandler}
                onClick={tooltipClickHandler}
                styleOverrides={tooltipStyleOverrides}
              >
                <p data-testid="toolTipInfo" id="infoToolTip">
                  {getContent(
                    `messages.emailTooltip.${onboardOption.value || 'guest'}`,
                  )}
                </p>
              </Tooltip>
            )}
          </EmailAndTooltipContainer>
        )}
      </ValueAsFieldLabel>
      {showMessage && (
        <InfoMessage
          message={getContent('billing.accountExistsMessage', {
            logInLink: (
              <LogInLink linkType="link">
                {getContent('billing.logIn')}
              </LogInLink>
            ),
          })}
          testId="email-member-match-disclaimer"
          withoutIcon
        />
      )}
    </>
  );
};

Email.propTypes = {
  blurHandler: func.isRequired,
  changeHandler: func.isRequired,
  focusHandler: func.isRequired,
};

export { Email };
